import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiGrid, FiList, FiSearch, FiX } from 'react-icons/fi';
import { gameAPI } from '../../services/api';
import { useTelegram } from '../../hooks/useTelegram';
import GameCard from '../../components/GameCard/GameCard';

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const Page = styled.div`
  min-height: 100vh;
  background: #0a0a0f;
`;

const Header = styled.div`
  padding: 16px 16px 12px;
  position: sticky;
  top: 0;
  z-index: 10;
  background: rgba(10,10,15,0.95);
  backdrop-filter: blur(20px);
  border-bottom: 0.5px solid rgba(255,255,255,0.06);
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 12px;
  padding: 10px 14px;
  margin-bottom: 12px;
  gap: 10px;
  transition: all 0.2s;
  &:focus-within {
    border-color: #006FFF;
    background: rgba(0,111,255,0.05);
  }
`;

const SearchIcon = styled.div`
  color: rgba(255,255,255,0.35);
  display: flex;
  align-items: center;
`;

const SearchInput = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: #fff;
  font-size: 15px;
  &::placeholder {
    color: rgba(255,255,255,0.35);
  }
`;

const ClearBtn = styled.button`
  background: rgba(255,255,255,0.1);
  border: none;
  color: rgba(255,255,255,0.5);
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  &:active { opacity: 0.7; }
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: #fff;
  margin: 0;
`;

const ViewToggle = styled.div`
  display: flex;
  gap: 2px;
  background: rgba(255,255,255,0.06);
  padding: 3px;
  border-radius: 10px;
`;

const VBtn = styled.button`
  background: ${p => p.$on ? '#006FFF' : 'transparent'};
  color: ${p => p.$on ? '#fff' : 'rgba(255,255,255,0.4)'};
  border: none;
  padding: 6px 10px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.2s;
`;

const Filters = styled.div`
  display: flex;
  gap: 6px;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const Chip = styled.button`
  flex-shrink: 0;
  padding: 8px 16px;
  background: ${p => p.$on ? '#006FFF' : 'rgba(255,255,255,0.06)'};
  color: ${p => p.$on ? '#fff' : 'rgba(255,255,255,0.5)'};
  border: ${p => p.$on ? 'none' : '1px solid rgba(255,255,255,0.04)'};
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  &:active { transform: scale(0.97); }
`;

const Info = styled.div`
  padding: 12px 16px 0;
  font-size: 12px;
  color: rgba(255,255,255,0.3);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: ${p => p.$list ? '1fr' : '1fr 1fr'};
  gap: 12px;
  padding: 12px 16px;
`;

const LoadMore = styled.button`
  display: block;
  margin: 8px auto 24px;
  padding: 12px 32px;
  background: rgba(0,111,255,0.1);
  color: #006FFF;
  border: 1px solid rgba(0,111,255,0.2);
  border-radius: 14px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  &:active { transform: scale(0.97); background: rgba(0,111,255,0.15); }
`;

const Skeleton = styled.div`
  background: linear-gradient(90deg, #14141f 25%, #1a1a2e 50%, #14141f 75%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.5s infinite;
  border-radius: 14px;
  aspect-ratio: 3/4;
`;

const FILTERS = [
  { id: 'all', label: 'Все' },
  { id: 'PS5', label: 'PS5' },
  { id: 'PS4', label: 'PS4' },
  { id: 'sale', label: '🏷 Скидки' },
];

const SORTS = [
  { id: 'default', label: 'По умолчанию' },
  { id: 'newest', label: '🆕 По дате' },
  { id: 'price_asc', label: '💰 Дешевле' },
  { id: 'price_desc', label: '💎 Дороже' },
];

const PRICE_RANGES = [
  { id: '0-1000', label: 'До 1 000 ₽' },
  { id: '1000-2000', label: '1–2 тыс ₽' },
  { id: '2000-3000', label: '2–3 тыс ₽' },
  { id: '3000-999999', label: 'От 3000 ₽' },
];

const PROFILE_KEY = 'openps_profile';

const getProfileConsole = () => {
  try {
    const p = JSON.parse(localStorage.getItem(PROFILE_KEY));
    return p?.console || 'ps5';
  } catch { return 'ps5'; }
};

const Catalog = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { tg } = useTelegram();
  const [filter, setFilter] = useState(params.get('platform') || params.get('filter') || 'all');
  const [sort, setSort] = useState('default');
  const [priceRange, setPriceRange] = useState(params.get('maxPrice') ? `0-${params.get('maxPrice')}` : params.get('minPrice') ? `${params.get('minPrice')}-999999` : 'all');
  const [rusVoice, setRusVoice] = useState(false);
  const [rusScreen, setRusScreen] = useState(false);
  const [list, setList] = useState(false);
  const [games, setGames] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const userConsole = getProfileConsole();

  useEffect(() => {
    const goBack = () => navigate('/'); if (tg) { tg.setHeaderColor('#0a0a0f'); tg.BackButton.show(); tg.BackButton.onClick(goBack); } return () => { if (tg) { tg.BackButton.offClick(goBack); tg.BackButton.hide(); } };
  }, [tg, navigate]);

  useEffect(() => {
    // Handle URL params
    if (params.get('onSale')) setFilter('sale');
    const urlSort = params.get('sort');
    if (urlSort && ['newest', 'price_asc', 'price_desc', 'rating', 'popular'].includes(urlSort)) setSort(urlSort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setOffset(0);
    loadGames(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, sort, priceRange, rusVoice, rusScreen, searchQuery]);

  const loadGames = (off) => {
    setLoading(true);
    
    // Если есть поисковый запрос — используем API поиска
    if (searchQuery.trim()) {
      gameAPI.searchGames(searchQuery.trim()).then(res => {
        const g = res.games || res || [];
        setGames(g);
        setTotal(g.length);
        setOffset(g.length);
        setLoading(false);
      }).catch(() => setLoading(false));
      return;
    }
    
    const q = { limit: 20, offset: off };
    if (filter === 'PS5' || filter === 'PS4') q.platform = filter;
    // Если фильтр "all" и у пользователя PS4 — показываем только PS4 игры
    if (filter === 'all' && userConsole === 'ps4') q.platform = 'PS4';
    if (filter === 'sale') q.onSale = true;
    // Русский язык
    if (rusVoice) q.rusVoice = true;
    if (rusScreen) q.rusScreen = true;
    // Сортировка
    if (sort === 'newest') q.sort = 'newest';
    if (sort === 'price_asc') q.sort = 'price_asc';
    if (sort === 'price_desc') q.sort = 'price_desc';
    if (sort === 'rating' || sort === 'popular') q.sort = 'rating';

    // Price range filter
    if (priceRange && priceRange !== 'all') {
      const [pMin, pMax] = priceRange.split('-').map(Number);
      if (pMin > 0) q.minPrice = pMin;
      if (pMax < 999999) q.maxPrice = pMax;
    } else {
      if (params.get('maxPrice')) q.maxPrice = params.get('maxPrice');
      if (params.get('minPrice')) q.minPrice = params.get('minPrice');
    }
    if (params.get('genre')) q.genre = params.get('genre');
    if (params.get('type')) q.type = params.get('type');

    gameAPI.getGames(q).then(res => {
      const g = res.games || res;
      if (off === 0) setGames(g);
      else setGames(prev => [...prev, ...g]);
      setTotal(res.total || g.length);
      setOffset(off + g.length);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  return (
    <Page>
      <Header>
        <TopRow>
          <Title>Каталог игр</Title>
          <ViewToggle>
            <VBtn $on={!list} onClick={() => setList(false)}><FiGrid size={16} /></VBtn>
            <VBtn $on={list} onClick={() => setList(true)}><FiList size={16} /></VBtn>
          </ViewToggle>
        </TopRow>
        <SearchBar>
          <SearchIcon><FiSearch size={18} /></SearchIcon>
          <SearchInput 
            type="text"
            placeholder="Поиск игр..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <ClearBtn onClick={() => setSearchQuery('')}>
              <FiX size={12} />
            </ClearBtn>
          )}
        </SearchBar>
        <Filters>
          {FILTERS.map(f => (
            <Chip key={f.id} $on={filter === f.id} onClick={() => setFilter(f.id)}>{f.label}</Chip>
          ))}
        </Filters>
        <Filters style={{ marginTop: '8px' }}>
          {SORTS.map(s => (
            <Chip key={s.id} $on={sort === s.id} onClick={() => setSort(s.id)}>{s.label}</Chip>
          ))}
        </Filters>
        <Filters style={{ marginTop: '8px' }}>
          {PRICE_RANGES.map(p => (
            <Chip key={p.id} $on={priceRange === p.id} onClick={() => setPriceRange(priceRange === p.id ? 'all' : p.id)}>{p.label}</Chip>
          ))}
        </Filters>
        <Filters style={{ marginTop: '8px' }}>
          <Chip $on={rusVoice} onClick={() => setRusVoice(!rusVoice)}>🎙 Рус. озвучка</Chip>
          <Chip $on={rusScreen} onClick={() => setRusScreen(!rusScreen)}>📝 Рус. текст</Chip>
        </Filters>
      </Header>

      <Info>{total} игр</Info>

      <Grid $list={list}>
        {loading && games.length === 0
          ? [1,2,3,4,5,6].map(i => <Skeleton key={i} />)
          : games.map(g => <GameCard key={g.id} game={g} />)
        }
      </Grid>

      {games.length > 0 && games.length < total && (
        <LoadMore onClick={() => loadGames(offset)} disabled={loading}>
          {loading ? 'Загрузка...' : `Показать ещё (${total - games.length})`}
        </LoadMore>
      )}
    </Page>
  );
};

export default Catalog;
